import { WatchQueryFetchPolicy } from '@apollo/client';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { getKindFromProvider } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';
import {
  getLiabilityIntegrationPaymentFrequency,
  getLiabilityIntegrationPrefilledFields,
  hasLiabilityIntegrationPaymentDetails,
} from '@/modules/liabilities/LiabilityModal/LiabilityForm.integration';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import {
  LiabilityPaymentFrequency,
  LiabilityPaymentKind,
} from '@/types/schema';

import { useLiabilitiesStepIntegrationLiabilitiesQuery } from '../graphql/LiabilitiesStep.generated';
import { LiabilitiesStepFormShape } from '../LiabilitiesStep.types';

interface UseLiabilitiesStepDataOpts {
  householdId: string;
  linkedHouseholdAssetProvider: AssetIntegrationProviders | null;
  /**
   * Overrides Apollo's default. Callers that can change the household's
   * integration clients in the same session need this, or the cache hands
   * them the previous selection's rows back.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}

export function useLiabilitiesStepData({
  householdId,
  linkedHouseholdAssetProvider,
  fetchPolicy,
}: UseLiabilitiesStepDataOpts) {
  const integrationClientKind = linkedHouseholdAssetProvider
    ? getKindFromProvider(linkedHouseholdAssetProvider)
    : undefined;

  const { data, loading } = useLiabilitiesStepIntegrationLiabilitiesQuery({
    variables: {
      householdsLike: { id: householdId },
      integrationClientKind,
    },
    skip: !integrationClientKind,
    fetchPolicy,
  });

  const integrationLiabilities = useMemo(() => {
    return (
      data?.integrationLiabilities?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null) ?? []
    );
  }, [data]);

  const defaultValues: LiabilitiesStepFormShape = useMemo(() => {
    const liabilities: LiabilitiesStepFormShape['liabilities'] = {};

    for (const liability of integrationLiabilities) {
      const paymentFrequency = getLiabilityIntegrationPaymentFrequency(
        liability.paymentFrequency
      );
      const integrationPrefilledFields = getLiabilityIntegrationPrefilledFields(
        {
          paymentAmount: liability.recurringPaymentAmount,
          paymentFrequency: paymentFrequency,
          interestRate: liability.interestRate,
          termStartDate: liability.termStart,
          termEndDate: liability.termEnd,
        }
      );

      liabilities[liability.id] = {
        shouldImport: false,
        integrationLiabilityId: liability.id,
        name: liability.name,
        issuerId: NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
        recipientId: NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
        outstandingBalance: liability.value
          ? new Decimal(liability.value)
          : null,
        balanceAsOfDate: null,
        notes: '',
        hasDifferentPaymentSource: false,
        alternateSourceId: null,
        _showExpandedPaymentDetails: hasLiabilityIntegrationPaymentDetails(
          integrationPrefilledFields
        ),
        paymentKind: LiabilityPaymentKind.Amortized,
        paymentFrequency: paymentFrequency ?? LiabilityPaymentFrequency.Monthly,
        paymentAmount: liability.recurringPaymentAmount ?? null,
        interestRate: liability.interestRate ?? null,
        termStartDate: liability.termStart ?? null,
        termEndDate: liability.termEnd ?? null,
      };
    }

    return { liabilities };
  }, [integrationLiabilities]);

  return { integrationLiabilities, defaultValues, loading };
}
